import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { FormikProps } from 'formik';
import { toast } from 'react-toastify';
import {
    CommonEcwComponentProps,
    ECW_THINJ_MANUFACTURER_NOT_AVAILABLE,
    EcwFormValues,
    EcwManufacturerRecord,
    getOrderKey,
    isThinjOrderBlocked,
    RecordApiMessages,
    RouteFieldOptions,
    RouteFieldOptionsThinj,
    SelectUnitOption,
    SiteFieldOptions,
} from '../EcwConstants';
import useRecordUsage from '../../../Hooks/useRecordUsage';
import useEcwOrders from '../../../Hooks/useEcwOrders';
import { getErrorMessage } from '../../../utils/commonUtils';
import { Inventory, RecordUsageRes } from '../../../redux/recordUsage/recordUsageApiSlice';
import { Form, InputGroup, OverlayTrigger, Popover, Table } from 'react-bootstrap';
import BarcodeInfo from '../InfoComponents/BarcodeInfo';
import { useDispatch } from 'react-redux';
import {
    ecwSelectedOrderInfo,
    setEcwSelectedBarcodeData,
} from '../../../redux/ecwOrder/ecwOrderSlice';
import ItemNameWarningDialog from '../InfoComponents/ItemNameWarningDialog';
import EcwManufacturerWarningDialog from '../InfoComponents/EcwManufacturerWarningDialog';
import { useAppSelector } from '../../../redux/hooks';

interface EcwUsageDetailsFormProps extends CommonEcwComponentProps {
    formik: FormikProps<EcwFormValues>;
    isSelectedOrderProcessed: boolean;
    isThinjBlocked?: boolean;
    setRecordApiMsgs?: React.Dispatch<React.SetStateAction<RecordApiMessages | null>>;
}

const EcwUsageDetailsForm: React.FC<EcwUsageDetailsFormProps> = ({
    formik,
    setLoading,
    isSelectedOrderProcessed,
    isThinjBlocked,
    setRecordApiMsgs,
}) => {
    const dispatch = useDispatch();
    const barcodeTimeoutRef = useRef<NodeJS.Timeout>();
    const barcodeInputRef = useRef<HTMLInputElement>(null);
    const prevOrderKeyRef = useRef<string | number | null>(null);
    const selectedOrderInfo = useAppSelector(ecwSelectedOrderInfo);

    const [barcodeData, setBarcodeData] = useState<RecordUsageRes | null>(null);
    const [useUnitsList, setUseUnitsList] = useState<SelectUnitOption[]>([]);
    const [isItemNameWarning, setIsItemNameWarning] = useState<boolean>(false);
    const [isImmunization, setIsImmunization] = useState<boolean>(false);

    // THINJ Manufacturer Validation States
    const [scannedManufacturer, setScannedManufacturer] = useState<string>('');
    const [selectedManufacturer, setSelectedManufacturer] = useState<EcwManufacturerRecord | null>(null);
    const [manufacturerOptions, setManufacturerOptions] = useState<EcwManufacturerRecord[]>([]);
    const [isManufacturerWarning, setIsManufacturerWarning] = useState<boolean>(false);

    const { getBarcodeDetailsAPI, getBarcodeDetailsLoading } = useRecordUsage();
    const { validateManufacturerWithEcwAPI, validateManufacturerWithEcwLoading } = useEcwOrders();

    const { values, errors, touched, setFieldValue, handleChange, handleBlur, setFieldTouched, setFieldError, setSubmitting } = formik;

    const currentOrderKey = useMemo(() => {
        if (!values.orderId || !selectedOrderInfo) return null;
        return getOrderKey(selectedOrderInfo) ?? selectedOrderInfo.id ?? values.orderId ?? null;
    }, [values.orderId, selectedOrderInfo]);

    const latestSelectedOrderInfoRef = useRef(selectedOrderInfo);

    useEffect(() => {
        latestSelectedOrderInfoRef.current = selectedOrderInfo;
    }, [selectedOrderInfo]);

    const resetMfgValidationState = useCallback(() => {
        setScannedManufacturer('');
        setSelectedManufacturer(null);
        setManufacturerOptions([]);
        setIsManufacturerWarning(false);
        setFieldValue("itemInfo.manufacturerEcwCode", "");
        setRecordApiMsgs?.(prev => {
            if (!prev?.error?.length) return prev;
            const filtered = prev.error.filter(e => !e.includes("manufacturer is not available in eCW"));
            return filtered.length ? { ...prev, error: filtered } : null;
        });
    }, [setFieldValue, setRecordApiMsgs]);

    // Reset dialogs and manufacturer state on order change
    useEffect(() => {
        if (currentOrderKey !== prevOrderKeyRef.current) {
            prevOrderKeyRef.current = currentOrderKey;
            setIsItemNameWarning(false);
            resetMfgValidationState();
        }
    }, [currentOrderKey, resetMfgValidationState]);

    useEffect(() => {
        setLoading?.(getBarcodeDetailsLoading || validateManufacturerWithEcwLoading);
    }, [getBarcodeDetailsLoading, validateManufacturerWithEcwLoading, setLoading]);

    useEffect(() => {
        if (!values.barcode) {
            setBarcodeData(null);
            dispatch(setEcwSelectedBarcodeData(null));
            setUseUnitsList([]);
            setIsItemNameWarning(false);
            resetMfgValidationState();
        }
    }, [values.barcode, dispatch, resetMfgValidationState]);

    useEffect(() => {
        setFieldValue("itemInfo", {
            lotNumber: barcodeData?.lotNo ?? "",
            expiration: barcodeData?.expDate ?? "",
            ndcCode: barcodeData?.ndc ?? "",
            manufacturer: barcodeData?.manufacturer ?? "",
            manufacturerEcwCode: barcodeData?.manufacturerEcwCode ?? "",
            cvXCode: barcodeData?.cvx ?? "",
            mvxCode: barcodeData?.mvxCode ?? "",
            mvxManufacturer: barcodeData?.mvxManufacturer ?? "",
            stock: barcodeData?.list2 ?? "",
            itemName: barcodeData?.rxName ?? "",
            itemType: barcodeData?.list1 ?? "",
        });
    }, [barcodeData, setFieldValue]);

    useEffect(() => {
        setIsImmunization(!!(selectedOrderInfo?.orderType === 'VAC' && selectedOrderInfo?.immId));
    }, [selectedOrderInfo]);

    const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(e);
        if (scannedManufacturer || selectedManufacturer || manufacturerOptions.length > 0) {
            resetMfgValidationState();
        }
    };

    const handleBarcodeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (isFormDisabled) return;
        if (!e.target.value) {
            setFieldTouched("barcode", true, true);
            return;
        }

        const missingFields = [];
        if (!values.patID) missingFields.push('patID');
        if (!values.orderId) missingFields.push('orderId');

        if (missingFields.length) {
            missingFields.forEach(field => setFieldTouched(field, true));
            return;
        }

        // Clear previous timeout
        if (barcodeTimeoutRef.current) {
            clearTimeout(barcodeTimeoutRef.current);
        }

        // Debounce barcode API call
        barcodeTimeoutRef.current = setTimeout(async () => {
            try {
                const params = isImmunization ? { isImmunization: true } : {};
                const res: RecordUsageRes = await getBarcodeDetailsAPI({
                    ...values,
                    query: "true",
                }, params);

                if (res.status === "F") {
                    setFieldError("barcode", res.msg);
                    setFieldTouched("barcode", true, false);
                    setSubmitting(true);
                    return;
                }

                setSubmitting(false);

                // Item name warning check (kept for medicationRequest e.g. MED orders)
                const latestOrder = latestSelectedOrderInfoRef.current;
                if (latestOrder?.medicationRequest) {
                    const orderItemName = latestOrder.medicationRequest?.medicationName?.trim()?.toLowerCase();
                    const barcodeItemName = res?.rxName?.trim()?.toLowerCase();

                    if (orderItemName && orderItemName !== barcodeItemName) {
                        setIsItemNameWarning(true);
                    }
                }

                /* In API response we get unitName as unitCode, using unitName for value and label */
                const updatedUseUnitsList: SelectUnitOption[] = res?.useUnits?.map(({ unitName, isDefault }) => ({
                    value: unitName,
                    label: unitName,
                    isDefault: !!isDefault,
                })) ?? [];

                const defaultUnit = updatedUseUnitsList.find(u => u.isDefault) || updatedUseUnitsList[0] || null;

                setUseUnitsList(updatedUseUnitsList);
                setFieldValue("units", defaultUnit?.value || "");

                // Check order type: is it THINJ?
                const isThinjOrder = (latestOrder?.orderType === "THINJ" || values.orderType === "THINJ");

                if (!isThinjOrder) {
                    setBarcodeData(res);
                    dispatch(setEcwSelectedBarcodeData(res));
                    toast.success(res.msg);
                    return;
                }

                // --- THINJ order flow ---
                const scannedMfg = res?.manufacturer?.trim() || "";
                setScannedManufacturer(scannedMfg);

                // If manufacturer is empty on the scanned item
                if (!scannedMfg) {
                    setManufacturerOptions([]);
                    setSelectedManufacturer(null);
                    setRecordApiMsgs?.({
                        success: "",
                        error: [ECW_THINJ_MANUFACTURER_NOT_AVAILABLE],
                    });
                    setSubmitting(true);
                    const initialData: RecordUsageRes = { ...res, manufacturerEcwCode: "" };
                    setBarcodeData(initialData);
                    dispatch(setEcwSelectedBarcodeData(initialData));
                    return;
                }

                // Use common loading component during manufacturer validation
                setLoading?.(true);

                try {
                    const valRes = await validateManufacturerWithEcwAPI({ name: scannedMfg });
                    const matches: EcwManufacturerRecord[] = valRes?.result || [];
                    setManufacturerOptions(matches);

                    if (matches.length === 0) {
                        // Case A: 0 items found -> Show error in common error area below order info box
                        setSelectedManufacturer(null);
                        setRecordApiMsgs?.({
                            success: "",
                            error: [ECW_THINJ_MANUFACTURER_NOT_AVAILABLE],
                        });
                        setSubmitting(true);
                        const initialData: RecordUsageRes = { ...res, manufacturerEcwCode: "" };
                        setBarcodeData(initialData);
                        dispatch(setEcwSelectedBarcodeData(initialData));
                    } else if (matches.length === 1) {
                        // Case B: 1 item found
                        // Clear common error if previously set
                        setRecordApiMsgs?.(prev => {
                            if (!prev?.error?.length) return prev;
                            const filtered = prev.error.filter(e => !e.includes("manufacturer is not available in eCW"));
                            return filtered.length ? { ...prev, error: filtered } : null;
                        });

                        const singleMatch = matches[0];
                        const isExactMatch = scannedMfg.trim().toLowerCase() === (singleMatch?.name || "").trim().toLowerCase();

                        if (isExactMatch) {
                            // Exact match found -> Avoid warning and allow user to continue
                            setSelectedManufacturer(singleMatch);
                            setSubmitting(false);

                            const updatedData: RecordUsageRes = {
                                ...res,
                                manufacturer: singleMatch.name,
                                manufacturerEcwCode: singleMatch.manufacturerCode,
                            };
                            setBarcodeData(updatedData);
                            dispatch(setEcwSelectedBarcodeData(updatedData));

                            setFieldValue("itemInfo.manufacturer", singleMatch.name);
                            setFieldValue("itemInfo.manufacturerEcwCode", singleMatch.manufacturerCode);
                            setIsManufacturerWarning(false);
                        } else {
                            // Not an exact match -> Preselect single match and show warning dialog
                            setSelectedManufacturer(singleMatch);
                            setSubmitting(true);
                            const initialData: RecordUsageRes = { ...res, manufacturerEcwCode: "" };
                            setBarcodeData(initialData);
                            dispatch(setEcwSelectedBarcodeData(initialData));
                            setIsManufacturerWarning(true);
                        }
                    } else {
                        // Case C: > 1 items found -> user selection required
                        // Clear common error if previously set
                        setRecordApiMsgs?.(prev => {
                            if (!prev?.error?.length) return prev;
                            const filtered = prev.error.filter(e => !e.includes("manufacturer is not available in eCW"));
                            return filtered.length ? { ...prev, error: filtered } : null;
                        });

                        // Keep record disabled until selection is made and accepted
                        setSelectedManufacturer(null);
                        setSubmitting(true);
                        const initialData: RecordUsageRes = { ...res, manufacturerEcwCode: "" };
                        setBarcodeData(initialData);
                        dispatch(setEcwSelectedBarcodeData(initialData));

                        // Trigger warning dialog for selection
                        setIsManufacturerWarning(true);
                    }
                } catch (apiErr: any) {
                    setSelectedManufacturer(null);
                    const errMsg = getErrorMessage(apiErr) || "Failed to validate manufacturer with eCW.";
                    setRecordApiMsgs?.({
                        success: "",
                        error: [errMsg],
                    });
                    setSubmitting(true);
                    const initialData: RecordUsageRes = { ...res, manufacturerEcwCode: "" };
                    setBarcodeData(initialData);
                    dispatch(setEcwSelectedBarcodeData(initialData));
                } finally {
                    setLoading?.(false);
                }

                toast.success(res.msg);
            } catch (error) {
                console.error("Barcode lookup failed:", error);
            }
        }, 500);
    };

    const handleManufacturerWarningAction = useCallback((action: "accept" | "cancel", selectedMfg?: EcwManufacturerRecord) => {
        setIsManufacturerWarning(false);
        if (action === "cancel") {
            setSubmitting(true);
            setFieldValue("itemInfo.manufacturerEcwCode", "");
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                    barcodeInputRef.current.select();
                }
            }, 0);
        } else if (action === "accept" && selectedMfg) {
            setSelectedManufacturer(selectedMfg);
            setSubmitting(false);

            const updatedData = barcodeData ? {
                ...barcodeData,
                manufacturer: selectedMfg.name,
                manufacturerEcwCode: selectedMfg.manufacturerCode,
            } : null;

            setBarcodeData(updatedData);
            if (updatedData) {
                dispatch(setEcwSelectedBarcodeData(updatedData));
            }

            setFieldValue("itemInfo.manufacturer", selectedMfg.name);
            setFieldValue("itemInfo.manufacturerEcwCode", selectedMfg.manufacturerCode);
        }
    }, [barcodeData, dispatch, setFieldValue, setSubmitting]);

    const handleItemNameWarningAction = useCallback((action: "accept" | "cancel") => {
        setIsItemNameWarning(false);
        if (action === "cancel") {
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                    barcodeInputRef.current.select();
                }
            }, 0);
        }
    }, []);

    const routeOptions = useMemo(() => {
        return (selectedOrderInfo?.orderType === "THINJ" || values.orderType === "THINJ")
            ? RouteFieldOptionsThinj
            : RouteFieldOptions;
    }, [selectedOrderInfo?.orderType, values.orderType]);

    const isFormDisabled = isSelectedOrderProcessed || Boolean(isThinjBlocked ?? isThinjOrderBlocked(selectedOrderInfo));

    const quantityPopover = useMemo(() => (
        <Popover id="quantityPopover">
            <Popover.Body>
                <Table hover={true}>
                    <thead>
                        <tr>
                            <th>Quantity</th>
                            <th>Unit</th>
                            <th>Location</th>
                        </tr>
                    </thead>
                    <tbody>
                        {barcodeData?.inventory && barcodeData.inventory.length > 0 ? (
                            barcodeData.inventory.map((item: Inventory, index: number) => (
                                <tr key={index}>
                                    <td>{item?.netQty}</td>
                                    <td>{barcodeData?.uom}</td>
                                    <td>{item?.locationName}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', color: 'red' }}>
                                    No inventory found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Popover.Body>
        </Popover>
    ), [barcodeData?.inventory, barcodeData?.uom]);

    return (
        <div className='item-usage-info-box'>
            <h4 className="form-section-title">Enter Item Usage Details</h4>
            <Form.Group className='form-field'>
                <Form.Label>Barcode*</Form.Label>
                <Form.Control
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="Enter Barcode"
                    name="barcode"
                    value={values.barcode}
                    onChange={handleBarcodeChange}
                    onBlur={handleBarcodeBlur}
                    isInvalid={touched.barcode && !!errors.barcode}
                    disabled={isFormDisabled}
                />
                {errors.barcode && touched.barcode && (
                    <Form.Control.Feedback type="invalid">{errors.barcode}</Form.Control.Feedback>
                )}
            </Form.Group>
            <div className='form-field-wrapper'>
                <Form.Group className='form-field'>
                    <Form.Label>Quantity*</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="number"
                            placeholder="Enter Quantity"
                            name="qty"
                            value={values.qty}
                            onChange={handleChange}
                            onBlur={(e) => {
                                if (!isItemNameWarning) {
                                    handleBlur(e);
                                }
                            }}
                            isInvalid={touched.qty && !!errors.qty}
                            disabled={isFormDisabled}
                        />
                        {useUnitsList?.length > 0 && (
                            <Form.Select
                                aria-label="Select Quantity"
                                name="units"
                                className="unit-select"
                                value={values.units}
                                onChange={handleChange}
                                disabled={isFormDisabled}
                                title={useUnitsList?.find(item => item.value === values.units)?.label || ""}
                            >
                                {useUnitsList.map((unit: SelectUnitOption, index: number) => (
                                    <option key={index} value={unit.value}>
                                        {unit.label}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </InputGroup>
                    {errors.qty && touched.qty && (
                        <Form.Control.Feedback type="invalid">{errors.qty}</Form.Control.Feedback>
                    )}
                    {barcodeData?.inventory && (
                        <OverlayTrigger trigger="hover" placement="bottom" overlay={quantityPopover}>
                            <Form.Text id="quantity-hint" muted>Inventory</Form.Text>
                        </OverlayTrigger>
                    )}
                </Form.Group>
                <Form.Group className='form-field'>
                    <Form.Label>Waste</Form.Label>
                    <Form.Control
                        type="number"
                        placeholder="Enter Waste"
                        name="waste"
                        value={values.waste}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.waste && !!errors.waste}
                        disabled={isFormDisabled}
                    />
                    {errors.waste && touched.waste && (
                        <Form.Control.Feedback type="invalid">{errors.waste}</Form.Control.Feedback>
                    )}
                </Form.Group>
            </div>
            <Form.Group className='form-field'>
                <Form.Label>Route*</Form.Label>
                <Select
                    name='route'
                    options={routeOptions}
                    classNamePrefix="react-select"
                    placeholder="Please select route"
                    isDisabled={isFormDisabled}
                    onChange={(selectedItem) => setFieldValue("route", selectedItem?.value)}
                    onBlur={() => setFieldTouched("route", true, true)}
                    value={routeOptions.find(item => item.value === values.route) || null}
                    className={`${touched.route && errors.route ? "is-invalid-border" : ""} react-select-container`}
                />
                {errors.route && touched.route && (
                    <Form.Control.Feedback type="invalid">{errors.route}</Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group className='form-field'>
                <Form.Label>Site*</Form.Label>
                <Select
                    name='site'
                    options={SiteFieldOptions}
                    classNamePrefix="react-select"
                    placeholder="Please select site"
                    isDisabled={isFormDisabled}
                    onChange={(selectedItem) => setFieldValue("site", selectedItem?.value)}
                    onBlur={() => setFieldTouched("site", true, true)}
                    value={SiteFieldOptions.find(item => item.value === values.site) || null}
                    className={`${touched.site && errors.site ? "is-invalid-border" : ""} react-select-container`}
                />
                {errors.site && touched.site && (
                    <Form.Control.Feedback type="invalid">{errors.site}</Form.Control.Feedback>
                )}
            </Form.Group>

            <BarcodeInfo
                barcodeInfo={barcodeData}
                canChangeManufacturer={Boolean(selectedOrderInfo?.orderType === "THINJ" && manufacturerOptions.length > 1 && selectedManufacturer)}
                onOpenManufacturerModal={() => setIsManufacturerWarning(true)}
            />

            {isManufacturerWarning && (
                <EcwManufacturerWarningDialog
                    show={isManufacturerWarning}
                    scannedManufacturer={scannedManufacturer}
                    manufacturers={manufacturerOptions}
                    selectedManufacturer={selectedManufacturer}
                    onAccept={(selectedMfg) => handleManufacturerWarningAction("accept", selectedMfg)}
                    onCancel={() => handleManufacturerWarningAction("cancel")}
                />
            )}

            {isItemNameWarning && (
                <ItemNameWarningDialog
                    show={isItemNameWarning}
                    onAccept={() => handleItemNameWarningAction("accept")}
                    onCancel={() => handleItemNameWarningAction("cancel")}
                    orderItemName={selectedOrderInfo?.medicationRequest?.medicationName ?? ""}
                    barcodeItemName={barcodeData?.rxName ?? ""}
                />
            )}
        </div>
    );
};

export default EcwUsageDetailsForm;
